import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  Badge,
  ContentTypeBadge,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { deleteChannel, getChannelList } from "../../../services/channels";
import { capitalize, matches, newestFirst } from "../../../utils/list";
import { canEditContent } from "../../../utils/roles";

const ChannelList = () => {
  const canEdit = canEditContent("channels");
  const history = useHistory();
  const { t } = useTranslation();

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [channelToDelete, setChannelToDelete] = useState(null);

  const loadChannels = () =>
    getChannelList().then((result) => {
      setChannels(newestFirst(result?.allChannels) || []);
      setLoading(false);
    });

  useEffect(() => {
    loadChannels();
  }, []);

  const filtered = useMemo(
    () =>
      channels.filter(
        (channel) =>
          matches(channel?.title, search) &&
          (status === "all" ||
            (status === "active") === (channel?.status === true))
      ),
    [channels, search, status]
  );
  const pagination = usePagination(filtered, 10, [search, status]);

  const handleDelete = async (channel_id) => {
    try {
      await deleteChannel(channel_id);
      toast.success("Channel Deleted Successfully!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
      await loadChannels();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Live TV channels")}
        subtitle={loading ? " " : `${channels.length} ${t("channels")}`}
        actions={
          canEdit && (
            <Link to="/dashboard/add-channel" className="ui-btn">
              <i className="ri-add-line" aria-hidden="true"></i>
              {t("Add Channel")}
            </Link>
          )
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search channel . . .")}
          />
          <Segmented
            label={t("status")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("All") },
              { value: "active", label: t("Active") },
              { value: "inactive", label: t("Inactive") },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-signal-tower-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Channel")}</th>
                    <th>{t("content type")}</th>
                    <th>{t("Channel Type")}</th>
                    <th>{t("genre")}</th>
                    <th>
                      {t("DVR")} · {t("Latency")}
                    </th>
                    <th>{t("status")}</th>
                    {canEdit && (
                      <th className="ui-cell-actions">
                        <span className="sr-only">{t("action")}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((channel) => {
                    const genres = channel?.genre
                      ?.map((g) => g.name)
                      .join(", ");
                    return (
                      <tr key={channel?._id}>
                        <td>
                          <div className="ui-media">
                            <img
                              className="ui-media-thumb"
                              src={
                                channel?.thumbnail?.static_thumbnail_url || ""
                              }
                              alt=""
                              loading="lazy"
                            />
                            <div style={{ minWidth: 0 }}>
                              <span
                                className="ui-media-title"
                                title={channel?.title}
                              >
                                {channel?.title}
                              </span>
                              <span className="ui-media-meta">
                                {[
                                  capitalize(channel?.default_language),
                                  channel?.monetization
                                    ? t("Monetized")
                                    : t("Not monetized"),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <ContentTypeBadge type={channel?.content_type} />
                        </td>
                        <td>
                          <Badge
                            color={
                              channel?.channel_type === "m3u"
                                ? "violet"
                                : "blue"
                            }
                          >
                            {channel?.channel_type === "m3u"
                              ? "M3U"
                              : t("Encoder")}
                          </Badge>
                        </td>
                        <td>
                          <span className="ui-clamp" title={genres}>
                            {genres}
                          </span>
                        </td>
                        <td className="ui-cell-muted">
                          {channel?.dvr === "on" ? "On" : "Off"} ·{" "}
                          {channel?.latency !== "" ? channel?.latency : "none"}
                        </td>
                        <td>
                          <StatusBadge active={channel?.status === true} />
                        </td>
                        {canEdit && (
                          <td className="ui-cell-actions">
                            <IconButton
                              icon="ri-pencil-line"
                              label={t("Edit")}
                              onClick={() =>
                                history.push(
                                  `/dashboard/update-channel/${channel?._id}`
                                )
                              }
                            />
                            <IconButton
                              icon="ri-delete-bin-line"
                              label={t("Delete")}
                              tone="danger"
                              onClick={() => setChannelToDelete(channel)}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {channelToDelete && (
        <Popup
          open={true}
          setOpen={() => setChannelToDelete(null)}
          deleteHandle={() => handleDelete(channelToDelete._id)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")} {channelToDelete?.title} ?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default ChannelList;
